//
// Device quality tiers for the /contact WebGL sand scene. Same shape as the
// koi tiers: the cost is fragment-bound (one full-screen shader), so the dials
// are DPR, footprint-texture resolution, and per-pixel feature flags. `lite`
// swaps the live shader for a static baked image.

export type SandTierId = 'premium' | 'standard' | 'lite';

export interface SandTier {
  id: SandTierId;
  /** devicePixelRatio cap. */
  dprCap: number;
  /** Footprint DataTexture resolution = viewport / footprintScale. */
  footprintScale: number;
  /** Concentric rings + parallel grooves in the shader. */
  rings: boolean;
  /** Blinn specular highlight on ridge crests. */
  specular: boolean;
  /** Fine film grain. */
  grain: boolean;
  /** Ridge-crest bloom. */
  bloom: boolean;
  /** Render the static baked image instead of the live shader. */
  staticFallback: boolean;
}

export const SAND_TIERS: Record<SandTierId, SandTier> = {
  premium:  { id: 'premium',  dprCap: 2,   footprintScale: 4, rings: true,  specular: true,  grain: true,  bloom: true,  staticFallback: false },
  standard: { id: 'standard', dprCap: 2,   footprintScale: 4, rings: true,  specular: true,  grain: true,  bloom: false, staticFallback: false },
  lite:     { id: 'lite',     dprCap: 1.5, footprintScale: 6, rings: false, specular: false, grain: false, bloom: false, staticFallback: true },
};

const ORDER: SandTierId[] = ['lite', 'standard', 'premium'];

export function lowerSandTier(id: SandTierId): SandTierId {
  const i = ORDER.indexOf(id);
  return ORDER[Math.max(0, i - 1)];
}

export function detectSandTier(): SandTierId {
  if (typeof window === 'undefined') return 'standard';

  const override = new URLSearchParams(window.location.search).get('tier');
  if (override === 'premium' || override === 'standard' || override === 'lite') {
    return override;
  }

  const coarse =
    typeof window.matchMedia === 'function' && window.matchMedia('(pointer: coarse)').matches;
  const w = window.innerWidth;
  if (w < 768 || coarse) return 'lite';

  const cores = navigator.hardwareConcurrency || 4;
  const mem = (navigator as Navigator & { deviceMemory?: number }).deviceMemory ?? 4;

  if (cores >= 8 && mem >= 8 && w >= 1280) return 'premium';
  return 'standard';
}
